/**
 * Dark and light, with the choice remembered.
 *
 * The theme is stamped as `data-theme` on <html> so CSS can override the
 * `prefers-color-scheme` default in *both* directions — a user who prefers dark
 * at the OS level must still be able to choose light here.
 *
 * Applied in an inline script in index.html as well, so the first paint is
 * already correct and there is no flash of the wrong theme.
 */

import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@/shared/components/icons'

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'context-graph.theme'

export function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    /* storage may be blocked */
  }
  // The reference uses a light workspace; explicit preferences still win.
  return 'light'
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
  // Keeps form controls and scrollbars in step with the chosen theme.
  document.documentElement.style.colorScheme = theme
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme())

  useEffect(() => {
    applyTheme(theme)
    try {
      window.localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      /* ignore */
    }
  }, [theme])

  return [theme, setTheme]
}

export function ThemeToggle({ compact }: { compact?: boolean }) {
  const [theme, setTheme] = useTheme()
  const next = theme === 'dark' ? 'light' : 'dark'

  const toggle = useCallback(() => setTheme(next), [next, setTheme])

  return (
    <button
      type="button"
      className="btn btn-ghost"
      style={{ justifyContent: compact ? 'center' : 'flex-start', width: '100%' }}
      onClick={toggle}
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? <Icon.sun className="navicon" /> : <Icon.moon className="navicon" />}
      {!compact && <span className="navtext">{next === 'light' ? 'Light mode' : 'Dark mode'}</span>}
    </button>
  )
}
